/**
 * AUTO-GENERATED — DO NOT EDIT
 *
 * Generated from the AsyncAPI spec at specs/api/
 * Run `npm run monsterrat:generate:contracts` to regenerate.
 */

import { z } from 'zod';

// ══════════════════════════════════════════════════════════════
// Schema Version
// ══════════════════════════════════════════════════════════════

/** Current contract schema version. Sent by mobile in all client→server events. */
export const SCHEMA_VERSION = 1;

// ══════════════════════════════════════════════════════════════
// Client → Server Events
// ══════════════════════════════════════════════════════════════

/** Join a session room for peer events and activity indicators */
export const JoinSessionPayloadSchema = z.object({
    v: z.number().int(),
    sessionId: z.string(),
  });
export type JoinSessionPayload = z.infer<typeof JoinSessionPayloadSchema>;

export const JoinSessionAckSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true) }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['NOT_FOUND', 'FORBIDDEN', 'VALIDATION_FAILED'] as const),
    message: z.string(),
    category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  }),
]);
export type JoinSessionAck = z.infer<typeof JoinSessionAckSchema>;

/** Leave a session room */
export const LeaveSessionPayloadSchema = z.object({
    v: z.number().int(),
    sessionId: z.string(),
  });
export type LeaveSessionPayload = z.infer<typeof LeaveSessionPayloadSchema>;

/** Start AI-initiated opening for a new session (AI speaks first) */
export const StartSessionPayloadSchema = z.object({
    v: z.number().int(),
    sessionId: z.string(),
  });
export type StartSessionPayload = z.infer<typeof StartSessionPayloadSchema>;

export const StartSessionAckSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true) }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['NOT_FOUND', 'FORBIDDEN', 'SESSION_BUSY', 'VALIDATION_FAILED'] as const),
    message: z.string(),
    category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  }),
]);
export type StartSessionAck = z.infer<typeof StartSessionAckSchema>;

/** Send a text message with optional pronunciation data */
export const SendMessagePayloadSchema = z.object({
    v: z.number().int(),
    sessionId: z.string(),
    text: z.string().min(1).max(5000),
    pronunciationData: z.object({
      accuracyScore: z.number(),
      pronunciationScore: z.number(),
      fluencyScore: z.number(),
      completenessScore: z.number(),
      prosodyScore: z.number().optional(),
      profile: z.enum(['word', 'phrase', 'sentence', 'free']).optional(),
      words: z.array(z.object({
        word: z.string(),
        accuracyScore: z.number(),
        errorType: z.string().optional(),
        phonemes: z.array(z.object({
          phoneme: z.string(),
          accuracyScore: z.number(),
          nbestPhonemes: z.array(z.object({
            phoneme: z.string(),
            score: z.number(),
          })).optional(),
        })).optional(),
        syllables: z.array(z.object({
          syllable: z.string(),
          accuracyScore: z.number(),
        })).optional(),
        prosodyFeedback: z.object({
          unexpectedBreakConfidence: z.number().optional(),
          missingBreakConfidence: z.number().optional(),
          monotoneConfidence: z.number().optional(),
        }).optional(),
      })).optional(),
    }).optional(),
    pronunciationRaw: z.string().optional(),
    userMessageId: z.string().uuid().optional(),
  });
export type SendMessagePayload = z.infer<typeof SendMessagePayloadSchema>;

export const SendMessageAckSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true) }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['NOT_FOUND', 'FORBIDDEN', 'SESSION_BUSY', 'VALIDATION_FAILED'] as const),
    message: z.string(),
    category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  }),
]);
export type SendMessageAck = z.infer<typeof SendMessageAckSchema>;

/** Regenerate AI response for an existing user message */
export const RegenerateMessagePayloadSchema = z.object({
    v: z.number().int(),
    sessionId: z.string(),
    messageId: z.string(),
  });
export type RegenerateMessagePayload = z.infer<typeof RegenerateMessagePayloadSchema>;

export const RegenerateMessageAckSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true) }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['NOT_FOUND', 'FORBIDDEN', 'SESSION_BUSY', 'VALIDATION_FAILED'] as const),
    message: z.string(),
    category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  }),
]);
export type RegenerateMessageAck = z.infer<typeof RegenerateMessageAckSchema>;

/** Initiate voice agent flow (STT -> AI -> TTS) */
export const SendAudioPayloadSchema = z.object({
    v: z.number().int(),
    sessionId: z.string(),
  });
export type SendAudioPayload = z.infer<typeof SendAudioPayloadSchema>;

export const SendAudioAckSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true) }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['NOT_FOUND', 'FORBIDDEN', 'SESSION_BUSY'] as const),
    message: z.string(),
    category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  }),
]);
export type SendAudioAck = z.infer<typeof SendAudioAckSchema>;

/** Stream audio chunks for STT transcription */
export const AudioInputPayloadSchema = z.object({
    data: z.any(),
    format: z.enum(['pcm', 'wav', 'mp3']).optional(),
  });
export type AudioInputPayload = z.infer<typeof AudioInputPayloadSchema>;

/** Signal end of audio input stream */
export const AudioInputDonePayloadSchema = z.object({});
export type AudioInputDonePayload = z.infer<typeof AudioInputDonePayloadSchema>;

/** Report user activity (typing, recording, viewing) */
export const ActivityPayloadSchema = z.object({
    v: z.number().int(),
    sessionId: z.string(),
    type: z.enum(['typing', 'stopped_typing', 'recording', 'viewing']),
  });
export type ActivityPayload = z.infer<typeof ActivityPayloadSchema>;

// ══════════════════════════════════════════════════════════════
// Server → Sender Events
// ══════════════════════════════════════════════════════════════

/** Single AI response token (streamed in real-time) */
export const TokenPayloadSchema = z.object({
    text: z.string(),
  });
export type TokenPayload = z.infer<typeof TokenPayloadSchema>;

/** Confirms both user and AI messages persisted to database */
export const MessageSavedPayloadSchema = z.object({
    userMessageId: z.string(),
    aiMessageId: z.string(),
  });
export type MessageSavedPayload = z.infer<typeof MessageSavedPayloadSchema>;

/** Text stream finished (analysis may still be running) */
export const StreamCompletePayloadSchema = z.object({
    text: z.string().optional(),
    directives: z.object({
      question: z.object({
        text: z.string(),
        options: z.array(z.string()),
      }).optional(),
      pronunciation_assessment: z.object({
        reference_text: z.string().optional(),
      }).optional(),
      narrate: z.object({
        cue: z.string().optional(),
      }).optional(),
      narrate_reply: z.object({
        speaker_name: z.string().optional(),
        speaker_line: z.string().optional(),
        cue: z.string().optional(),
      }).optional(),
      narrate_react: z.object({
        speaker_name: z.string().optional(),
        reaction_line: z.string().optional(),
        cue: z.string().optional(),
      }).optional(),
    }).optional(),
  });
export type StreamCompletePayload = z.infer<typeof StreamCompletePayloadSchema>;

/** Message processing cycle complete */
export const DonePayloadSchema = z.object({});
export type DonePayload = z.infer<typeof DonePayloadSchema>;

/** start_session processing failed asynchronously (post-ack) */
export const StartSessionErrorPayloadSchema = z.object({
  code: z.enum(['AI_SERVICE_ERROR', 'AI_STREAM_ERROR', 'INTERNAL_ERROR'] as const),
  message: z.string(),
  category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  correlationId: z.string().optional(),
});
export type StartSessionErrorPayload = z.infer<typeof StartSessionErrorPayloadSchema>;

/** send_message processing failed asynchronously (post-ack) */
export const SendMessageErrorPayloadSchema = z.object({
  code: z.enum(['STREAM_ERROR', 'AI_SERVICE_ERROR', 'AI_PROVIDER_ERROR', 'AI_EMPTY_RESPONSE', 'AI_STREAM_ERROR', 'INTERNAL_ERROR'] as const),
  message: z.string(),
  category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  correlationId: z.string().optional(),
});
export type SendMessageErrorPayload = z.infer<typeof SendMessageErrorPayloadSchema>;

/** regenerate_message processing failed asynchronously (post-ack) */
export const RegenerateMessageErrorPayloadSchema = z.object({
  code: z.enum(['STREAM_ERROR', 'AI_SERVICE_ERROR', 'AI_PROVIDER_ERROR', 'AI_EMPTY_RESPONSE', 'AI_STREAM_ERROR', 'INTERNAL_ERROR'] as const),
  message: z.string(),
  category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  correlationId: z.string().optional(),
});
export type RegenerateMessageErrorPayload = z.infer<typeof RegenerateMessageErrorPayloadSchema>;

/** send_audio AI-pipeline failure (post-ack). TTS / STT subsystem failures surface via `audio_error` / `transcription_error` below. */
export const SendAudioErrorPayloadSchema = z.object({
  code: z.enum(['AI_SERVICE_ERROR', 'AI_STREAM_ERROR', 'INTERNAL_ERROR'] as const),
  message: z.string(),
  category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  correlationId: z.string().optional(),
});
export type SendAudioErrorPayload = z.infer<typeof SendAudioErrorPayloadSchema>;

/** Analysis phase started */
export const AnalyzingPayloadSchema = z.object({});
export type AnalyzingPayload = z.infer<typeof AnalyzingPayloadSchema>;

/** Analysis results available */
export const AnalysisCompletePayloadSchema = z.object({
    analysis: z.object({
      grammarErrors: z.array(z.object({
        original: z.string(),
        correction: z.string(),
        explanation: z.string(),
        type: z.string(),
        severity: z.enum(['critical', 'moderate', 'minor']).optional(),
      })).optional(),
      lessonReferences: z.array(z.object({
        slug: z.string(),
        title: z.string(),
      })).optional(),
      nativeExpression: z.object({
        nativeVersion: z.string(),
        explanation: z.string(),
        differences: z.array(z.string()).optional(),
      }).optional(),
      encouragement: z.string().optional(),
    }),
  });
export type AnalysisCompletePayload = z.infer<typeof AnalysisCompletePayloadSchema>;

/** Analysis failed or timed out (non-fatal) */
export const AnalysisFailedPayloadSchema = z.object({
    error: z.string(),
  });
export type AnalysisFailedPayload = z.infer<typeof AnalysisFailedPayloadSchema>;

/** Streamed audio chunk from TTS synthesis */
export const AudioChunkPayloadSchema = z.object({
    data: z.any(),
    format: z.enum(['mp3', 'pcm']),
    sequence: z.number().int(),
  });
export type AudioChunkPayload = z.infer<typeof AudioChunkPayloadSchema>;

/** All TTS audio chunks have been sent */
export const AudioDonePayloadSchema = z.object({
    totalChunks: z.number().int(),
  });
export type AudioDonePayload = z.infer<typeof AudioDonePayloadSchema>;

/** TTS synthesis failed */
export const AudioErrorPayloadSchema = z.object({
    code: z.enum(['TTS_ERROR', 'INTERNAL_ERROR']),
    message: z.string(),
    category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  });
export type AudioErrorPayload = z.infer<typeof AudioErrorPayloadSchema>;

/** Interim transcription result (may change) */
export const TranscriptionPartialPayloadSchema = z.object({
    text: z.string(),
  });
export type TranscriptionPartialPayload = z.infer<typeof TranscriptionPartialPayloadSchema>;

/** Final transcription with confidence score */
export const TranscriptionDonePayloadSchema = z.object({
    text: z.string(),
    confidence: z.number(),
  });
export type TranscriptionDonePayload = z.infer<typeof TranscriptionDonePayloadSchema>;

/** STT transcription failed */
export const TranscriptionErrorPayloadSchema = z.object({
    code: z.enum(['STT_ERROR', 'INTERNAL_ERROR']),
    message: z.string(),
    category: z.enum(['VALIDATION', 'BUSINESS', 'AUTH', 'UNEXPECTED']),
  });
export type TranscriptionErrorPayload = z.infer<typeof TranscriptionErrorPayloadSchema>;

/** New session created via flow transition */
export const SessionTransitionPayloadSchema = z.object({
    newSessionId: z.string(),
    flowType: z.string(),
    hasAnalysis: z.boolean(),
  });
export type SessionTransitionPayload = z.infer<typeof SessionTransitionPayloadSchema>;

// ══════════════════════════════════════════════════════════════
// Server → Peer Events (Broadcast)
// ══════════════════════════════════════════════════════════════

/** Another device in the session sent a message */
export const PeerMessageSavedPayloadSchema = z.object({
    sessionId: z.string(),
    userMessageId: z.string(),
    userText: z.string(),
  });
export type PeerMessageSavedPayload = z.infer<typeof PeerMessageSavedPayloadSchema>;

/** Another device's AI response completed */
export const PeerDonePayloadSchema = z.object({
    sessionId: z.string(),
    userMessageId: z.string(),
    userText: z.string(),
    aiMessageId: z.string(),
    aiText: z.string(),
  });
export type PeerDonePayload = z.infer<typeof PeerDonePayloadSchema>;

/** Another device's analysis completed */
export const PeerAnalysisCompletePayloadSchema = z.object({
    sessionId: z.string(),
    userMessageId: z.string(),
    analysis: z.object({
      grammarErrors: z.array(z.object({
        original: z.string(),
        correction: z.string(),
        explanation: z.string(),
        type: z.string(),
        severity: z.enum(['critical', 'moderate', 'minor']).optional(),
      })).optional(),
      lessonReferences: z.array(z.object({
        slug: z.string(),
        title: z.string(),
      })).optional(),
      nativeExpression: z.object({
        nativeVersion: z.string(),
        explanation: z.string(),
        differences: z.array(z.string()).optional(),
      }).optional(),
      encouragement: z.string().optional(),
    }),
  });
export type PeerAnalysisCompletePayload = z.infer<typeof PeerAnalysisCompletePayloadSchema>;

/** Another device's analysis failed */
export const PeerAnalysisFailedPayloadSchema = z.object({
    sessionId: z.string(),
    userMessageId: z.string(),
  });
export type PeerAnalysisFailedPayload = z.infer<typeof PeerAnalysisFailedPayloadSchema>;

/** Activity indicator from a session participant (user or bot) */
export const ParticipantActivityPayloadSchema = z.object({
    sessionId: z.string(),
    actor: z.object({
      kind: z.enum(['user', 'bot']),
      id: z.string(),
    }),
    type: z.enum(['typing', 'stopped_typing', 'recording', 'viewing']),
  });
export type ParticipantActivityPayload = z.infer<typeof ParticipantActivityPayloadSchema>;

// ══════════════════════════════════════════════════════════════
// Event Name Maps
// ══════════════════════════════════════════════════════════════

/** All client-to-server event names */
export const ClientEvents = {
  JoinSession: 'join_session',
  LeaveSession: 'leave_session',
  StartSession: 'start_session',
  SendMessage: 'send_message',
  RegenerateMessage: 'regenerate_message',
  SendAudio: 'send_audio',
  AudioInput: 'audio_input',
  AudioInputDone: 'audio_input_done',
  Activity: 'activity',
} as const;

/** All server-to-sender event names */
export const SenderEvents = {
  Token: 'token',
  MessageSaved: 'message_saved',
  StreamComplete: 'stream_complete',
  Done: 'done',
  StartSessionError: 'start_session_error',
  SendMessageError: 'send_message_error',
  RegenerateMessageError: 'regenerate_message_error',
  SendAudioError: 'send_audio_error',
  Analyzing: 'analyzing',
  AnalysisComplete: 'analysis_complete',
  AnalysisFailed: 'analysis_failed',
  AudioChunk: 'audio_chunk',
  AudioDone: 'audio_done',
  AudioError: 'audio_error',
  TranscriptionPartial: 'transcription_partial',
  TranscriptionDone: 'transcription_done',
  TranscriptionError: 'transcription_error',
  SessionTransition: 'session_transition',
} as const;

/** All server-to-peer event names */
export const PeerEvents = {
  PeerMessageSaved: 'peer_message_saved',
  PeerDone: 'peer_done',
  PeerAnalysisComplete: 'peer_analysis_complete',
  PeerAnalysisFailed: 'peer_analysis_failed',
  ParticipantActivity: 'participant_activity',
} as const;

/** Payload schemas keyed by event name — for runtime validation */
export const PayloadSchemas = {
  'join_session': JoinSessionPayloadSchema,
  'leave_session': LeaveSessionPayloadSchema,
  'start_session': StartSessionPayloadSchema,
  'send_message': SendMessagePayloadSchema,
  'regenerate_message': RegenerateMessagePayloadSchema,
  'send_audio': SendAudioPayloadSchema,
  'audio_input': AudioInputPayloadSchema,
  'audio_input_done': AudioInputDonePayloadSchema,
  'activity': ActivityPayloadSchema,
  'token': TokenPayloadSchema,
  'message_saved': MessageSavedPayloadSchema,
  'stream_complete': StreamCompletePayloadSchema,
  'done': DonePayloadSchema,
  'start_session_error': StartSessionErrorPayloadSchema,
  'send_message_error': SendMessageErrorPayloadSchema,
  'regenerate_message_error': RegenerateMessageErrorPayloadSchema,
  'send_audio_error': SendAudioErrorPayloadSchema,
  'analyzing': AnalyzingPayloadSchema,
  'analysis_complete': AnalysisCompletePayloadSchema,
  'analysis_failed': AnalysisFailedPayloadSchema,
  'audio_chunk': AudioChunkPayloadSchema,
  'audio_done': AudioDonePayloadSchema,
  'audio_error': AudioErrorPayloadSchema,
  'transcription_partial': TranscriptionPartialPayloadSchema,
  'transcription_done': TranscriptionDonePayloadSchema,
  'transcription_error': TranscriptionErrorPayloadSchema,
  'session_transition': SessionTransitionPayloadSchema,
  'peer_message_saved': PeerMessageSavedPayloadSchema,
  'peer_done': PeerDonePayloadSchema,
  'peer_analysis_complete': PeerAnalysisCompletePayloadSchema,
  'peer_analysis_failed': PeerAnalysisFailedPayloadSchema,
  'participant_activity': ParticipantActivityPayloadSchema,
} as const;

// ══════════════════════════════════════════════════════════════
// Typed Socket.IO Interfaces
// ══════════════════════════════════════════════════════════════

/** Server-to-client events — use as Server<ClientToServerEvents, ServerToClientEvents> */
export interface ServerToClientEvents {
  token: (data: TokenPayload) => void;
  message_saved: (data: MessageSavedPayload) => void;
  stream_complete: (data: StreamCompletePayload) => void;
  done: (data: DonePayload) => void;
  start_session_error: (data: StartSessionErrorPayload) => void;
  send_message_error: (data: SendMessageErrorPayload) => void;
  regenerate_message_error: (data: RegenerateMessageErrorPayload) => void;
  send_audio_error: (data: SendAudioErrorPayload) => void;
  analyzing: (data: AnalyzingPayload) => void;
  analysis_complete: (data: AnalysisCompletePayload) => void;
  analysis_failed: (data: AnalysisFailedPayload) => void;
  audio_chunk: (data: AudioChunkPayload) => void;
  audio_done: (data: AudioDonePayload) => void;
  audio_error: (data: AudioErrorPayload) => void;
  transcription_partial: (data: TranscriptionPartialPayload) => void;
  transcription_done: (data: TranscriptionDonePayload) => void;
  transcription_error: (data: TranscriptionErrorPayload) => void;
  session_transition: (data: SessionTransitionPayload) => void;
  peer_message_saved: (data: PeerMessageSavedPayload) => void;
  peer_done: (data: PeerDonePayload) => void;
  peer_analysis_complete: (data: PeerAnalysisCompletePayload) => void;
  peer_analysis_failed: (data: PeerAnalysisFailedPayload) => void;
  participant_activity: (data: ParticipantActivityPayload) => void;
}

/** Client-to-server events — use as Server<ClientToServerEvents, ServerToClientEvents> */
export interface ClientToServerEvents {
  join_session: (data: JoinSessionPayload, ack: (res: JoinSessionAck) => void) => void;
  leave_session: (data: LeaveSessionPayload) => void;
  start_session: (data: StartSessionPayload, ack: (res: StartSessionAck) => void) => void;
  send_message: (data: SendMessagePayload, ack: (res: SendMessageAck) => void) => void;
  regenerate_message: (data: RegenerateMessagePayload, ack: (res: RegenerateMessageAck) => void) => void;
  send_audio: (data: SendAudioPayload, ack: (res: SendAudioAck) => void) => void;
  audio_input: (data: AudioInputPayload) => void;
  audio_input_done: (data: AudioInputDonePayload) => void;
  activity: (data: ActivityPayload) => void;
}

/** Sender-only event map — for ConversationRoom.emitToSender */
export interface SenderEventMap {
  token: TokenPayload;
  message_saved: MessageSavedPayload;
  stream_complete: StreamCompletePayload;
  done: DonePayload;
  start_session_error: StartSessionErrorPayload;
  send_message_error: SendMessageErrorPayload;
  regenerate_message_error: RegenerateMessageErrorPayload;
  send_audio_error: SendAudioErrorPayload;
  analyzing: AnalyzingPayload;
  analysis_complete: AnalysisCompletePayload;
  analysis_failed: AnalysisFailedPayload;
  audio_chunk: AudioChunkPayload;
  audio_done: AudioDonePayload;
  audio_error: AudioErrorPayload;
  transcription_partial: TranscriptionPartialPayload;
  transcription_done: TranscriptionDonePayload;
  transcription_error: TranscriptionErrorPayload;
  session_transition: SessionTransitionPayload;
}

/** Peer event map — for ConversationRoom.emitToSession, emitToPeerDevices, broadcast */
export interface PeerEventMap {
  peer_message_saved: PeerMessageSavedPayload;
  peer_done: PeerDonePayload;
  peer_analysis_complete: PeerAnalysisCompletePayload;
  peer_analysis_failed: PeerAnalysisFailedPayload;
  participant_activity: ParticipantActivityPayload;
}

